import { useEffect, useMemo, useState } from 'react'

const BANDS = 36

interface EQCurveProps {
  width?: number
  height?: number
  lowGainDb: number
  lowFreq: number
  mid1GainDb: number
  mid1Freq: number
  mid1Q: number
  mid2GainDb: number
  mid2Freq: number
  mid2Q: number
  highGainDb: number
  highFreq: number
  sampleRate?: number
  getSpectrum?: (bands: number) => Float32Array
}

type BiquadCoeffs = {
  b0: number
  b1: number
  b2: number
  a0: number
  a1: number
  a2: number
}

const MIN_FREQ = 20
const MAX_FREQ = 20000
const MIN_DB = -18
const MAX_DB = 18

function normalize({ b0, b1, b2, a0, a1, a2 }: BiquadCoeffs) {
  return {
    b0: b0 / a0,
    b1: b1 / a0,
    b2: b2 / a0,
    a1: a1 / a0,
    a2: a2 / a0,
  }
}

function peaking(freq: number, q: number, gainDb: number, fs: number): BiquadCoeffs {
  const A = Math.pow(10, gainDb / 40)
  const w0 = (2 * Math.PI * freq) / fs
  const cosW0 = Math.cos(w0)
  const sinW0 = Math.sin(w0)
  const alpha = sinW0 / (2 * q)

  const b0 = 1 + alpha * A
  const b1 = -2 * cosW0
  const b2 = 1 - alpha * A
  const a0 = 1 + alpha / A
  const a1 = -2 * cosW0
  const a2 = 1 - alpha / A

  return { b0, b1, b2, a0, a1, a2 }
}

function shelfLow(freq: number, gainDb: number, fs: number): BiquadCoeffs {
  const A = Math.pow(10, gainDb / 40)
  const w0 = (2 * Math.PI * freq) / fs
  const cosW0 = Math.cos(w0)
  const sinW0 = Math.sin(w0)
  const S = 1
  const alpha = (sinW0 / 2) * Math.sqrt((A + 1 / A) * (1 / S - 1) + 2)
  const twoSqrtAAlpha = 2 * Math.sqrt(A) * alpha

  const b0 = A * ((A + 1) - (A - 1) * cosW0 + twoSqrtAAlpha)
  const b1 = 2 * A * ((A - 1) - (A + 1) * cosW0)
  const b2 = A * ((A + 1) - (A - 1) * cosW0 - twoSqrtAAlpha)
  const a0 = (A + 1) + (A - 1) * cosW0 + twoSqrtAAlpha
  const a1 = -2 * ((A - 1) + (A + 1) * cosW0)
  const a2 = (A + 1) + (A - 1) * cosW0 - twoSqrtAAlpha

  return { b0, b1, b2, a0, a1, a2 }
}

function shelfHigh(freq: number, gainDb: number, fs: number): BiquadCoeffs {
  const A = Math.pow(10, gainDb / 40)
  const w0 = (2 * Math.PI * freq) / fs
  const cosW0 = Math.cos(w0)
  const sinW0 = Math.sin(w0)
  const S = 1
  const alpha = (sinW0 / 2) * Math.sqrt((A + 1 / A) * (1 / S - 1) + 2)
  const twoSqrtAAlpha = 2 * Math.sqrt(A) * alpha

  const b0 = A * ((A + 1) + (A - 1) * cosW0 + twoSqrtAAlpha)
  const b1 = -2 * A * ((A - 1) + (A + 1) * cosW0)
  const b2 = A * ((A + 1) + (A - 1) * cosW0 - twoSqrtAAlpha)
  const a0 = (A + 1) - (A - 1) * cosW0 + twoSqrtAAlpha
  const a1 = 2 * ((A - 1) - (A + 1) * cosW0)
  const a2 = (A + 1) - (A - 1) * cosW0 - twoSqrtAAlpha

  return { b0, b1, b2, a0, a1, a2 }
}

function magnitudeAt(coeffs: ReturnType<typeof normalize>, w: number) {
  const cosW = Math.cos(w)
  const sinW = Math.sin(w)
  const cos2W = Math.cos(2 * w)
  const sin2W = Math.sin(2 * w)

  const numReal = coeffs.b0 + coeffs.b1 * cosW + coeffs.b2 * cos2W
  const numImag = -(coeffs.b1 * sinW + coeffs.b2 * sin2W)
  const denReal = 1 + coeffs.a1 * cosW + coeffs.a2 * cos2W
  const denImag = -(coeffs.a1 * sinW + coeffs.a2 * sin2W)

  const num = numReal * numReal + numImag * numImag
  const den = denReal * denReal + denImag * denImag
  if (den === 0) return 0
  return Math.sqrt(num / den)
}

function clamp(value: number, min: number, max: number) {
  return Math.max(min, Math.min(max, value))
}

/** Returns an hsla colour for a spectrum bar index (0 = red/warm, BANDS-1 = cyan/cool) */
function spectrumColor(i: number, total: number, alpha: number) {
  const hue = (i / (total - 1)) * 180
  return `hsla(${hue}, 80%, 55%, ${alpha})`
}

function logScale(value: number) {
  const minLog = Math.log10(MIN_FREQ)
  const maxLog = Math.log10(MAX_FREQ)
  const vLog = Math.log10(value)
  return (vLog - minLog) / (maxLog - minLog)
}

export function EQCurve({
  width = 180,
  height = 90,
  lowGainDb,
  lowFreq,
  mid1GainDb,
  mid1Freq,
  mid1Q,
  mid2GainDb,
  mid2Freq,
  mid2Q,
  highGainDb,
  highFreq,
  sampleRate = 48000,
  getSpectrum,
}: EQCurveProps) {
  const [spectrumBars, setSpectrumBars] = useState<Float32Array | null>(null)

  const path = useMemo(() => {
    const low = normalize(shelfLow(lowFreq, lowGainDb, sampleRate))
    const mid1 = normalize(peaking(mid1Freq, mid1Q, mid1GainDb, sampleRate))
    const mid2 = normalize(peaking(mid2Freq, mid2Q, mid2GainDb, sampleRate))
    const high = normalize(shelfHigh(highFreq, highGainDb, sampleRate))

    const points: string[] = []
    const total = 120
    for (let i = 0; i <= total; i += 1) {
      const t = i / total
      const freq = MIN_FREQ * Math.pow(MAX_FREQ / MIN_FREQ, t)
      const w = (2 * Math.PI * freq) / sampleRate

      let mag = 1
      mag *= magnitudeAt(low, w)
      mag *= magnitudeAt(mid1, w)
      mag *= magnitudeAt(mid2, w)
      mag *= magnitudeAt(high, w)

      let db = 20 * Math.log10(mag)
      if (!Number.isFinite(db)) db = MIN_DB
      db = clamp(db, MIN_DB, MAX_DB)

      const x = logScale(freq) * width
      const y = ((MAX_DB - db) / (MAX_DB - MIN_DB)) * height
      points.push(`${i === 0 ? 'M' : 'L'}${x.toFixed(2)},${y.toFixed(2)}`)
    }

    return points.join(' ')
  }, [
    height,
    highFreq,
    highGainDb,
    lowFreq,
    lowGainDb,
    mid1Freq,
    mid1GainDb,
    mid1Q,
    mid2Freq,
    mid2GainDb,
    mid2Q,
    sampleRate,
    width,
  ])

  // Animate spectrum
  useEffect(() => {
    if (!getSpectrum) return
    let raf = 0
    const tick = () => {
      setSpectrumBars(getSpectrum(BANDS))
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [getSpectrum])

  const zeroY = ((MAX_DB - 0) / (MAX_DB - MIN_DB)) * height

  // Build spectrum bar rects
  const barWidth = width / BANDS
  const spectrumRects = spectrumBars
    ? Array.from({ length: BANDS }, (_, i) => {
        const value = spectrumBars[i] || 0
        const barH = Math.max(0, value * height)
        const x = i * barWidth + 0.5
        const y = height - barH
        return (
          <rect
            key={i}
            x={x}
            y={y}
            width={Math.max(0.5, barWidth - 1)}
            height={barH}
            fill={spectrumColor(i, BANDS, 0.25)}
          />
        )
      })
    : null

  return (
    <div className="eq-curve" aria-hidden="true">
      <svg viewBox={`0 0 ${width} ${height}`} role="img">
        {/* Spectrum bars behind everything */}
        {spectrumRects}
        <g className="eq-curve__labels">
          {[20, 50, 100, 200, 500, 1000, 2000, 5000, 10000, 20000].map((freq) => (
            <text key={freq} x={logScale(freq) * width} y={8} textAnchor="middle">
              {freq >= 1000 ? `${freq / 1000}k` : freq}
            </text>
          ))}
        </g>
        <line className="eq-curve__line" x1="0" y1={zeroY} x2={width} y2={zeroY} />
        <path className="eq-curve__path" d={path} />
      </svg>
    </div>
  )
}
